import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  AcademicCapIcon, 
  UserGroupIcon, 
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  UserCircleIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XMarkIcon,
  EyeIcon,
  ChevronUpIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';

const CoachesPrograms = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddCoachModal, setShowAddCoachModal] = useState(false);
  const [showAssignProgramModal, setShowAssignProgramModal] = useState(false);
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    speciality: 'musculation',
    experience: '',
    email: '',
    phone: '',
    status: 'active'
  });
  const [expandedCoach, setExpandedCoach] = useState(null);

  // Données factices pour les coachs
  const mockCoaches = [
    {
      id: 1,
      firstName: 'Karim',
      lastName: 'Benali',
      speciality: 'musculation',
      experience: '5 ans',
      email: 'karim.benali@example.com',
      phone: '+213 555 123 456',
      status: 'active',
      programs: [
        { id: 1, name: 'Programme Débutant', members: 15 },
        { id: 2, name: 'Force et Masse', members: 8 }
      ]
    },
    {
      id: 2,
      firstName: 'Samia',
      lastName: 'Hadjri',
      speciality: 'cardio',
      experience: '3 ans',
      email: 'samia.hadjri@example.com',
      phone: '+213 555 789 012',
      status: 'active',
      programs: [
        { id: 3, name: 'Cardio Intensif', members: 22 }
      ]
    },
    {
      id: 3,
      firstName: 'Yacine',
      lastName: 'Rahmouni',
      speciality: 'crossfit',
      experience: '7 ans',
      email: 'yacine.rahmouni@example.com',
      phone: '+213 555 345 678',
      status: 'vacation',
      programs: [
        { id: 4, name: 'Functional Training', members: 12 },
        { id: 5, name: 'Cross Training Pro', members: 6 }
      ]
    },
    {
      id: 4,
      firstName: 'Lina',
      lastName: 'Cherif',
      speciality: 'yoga',
      experience: '4 ans',
      email: 'lina.cherif@example.com',
      phone: '+213 555 901 234',
      status: 'active',
      programs: []
    }
  ];

  // Données factices pour les programmes
  const mockPrograms = [
    { id: 1, name: 'Programme Débutant', category: 'musculation', difficulty: 'beginner' },
    { id: 2, name: 'Force et Masse', category: 'musculation', difficulty: 'advanced' },
    { id: 3, name: 'Cardio Intensif', category: 'cardio', difficulty: 'intermediate' },
    { id: 4, name: 'Functional Training', category: 'fonctionnel', difficulty: 'intermediate' },
    { id: 5, name: 'Cross Training Pro', category: 'crossfit', difficulty: 'advanced' },
    { id: 6, name: 'Yoga Flow', category: 'yoga', difficulty: 'beginner' },
    { id: 7, name: 'HIIT Challenge', category: 'cardio', difficulty: 'advanced' }
  ];

  const filteredCoaches = mockCoaches.filter(coach => {
    const fullName = `${coach.firstName} ${coach.lastName}`.toLowerCase();
    return fullName.includes(searchTerm.toLowerCase()) || 
           coach.speciality.toLowerCase().includes(searchTerm.toLowerCase()) ||
           coach.email.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded-full text-xs">Actif</span>;
      case 'vacation':
        return <span className="px-2 py-1 bg-amber-500/20 text-amber-400 rounded-full text-xs">En congé</span>;
      case 'suspended':
        return <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded-full text-xs">Suspendu</span>;
      default:
        return <span className="px-2 py-1 bg-gray-500/20 text-gray-400 rounded-full text-xs">Inconnu</span>;
    }
  };

  const getSpecialityName = (speciality) => {
    switch (speciality) {
      case 'musculation': return 'Musculation';
      case 'cardio': return 'Cardio';
      case 'crossfit': return 'CrossFit';
      case 'yoga': return 'Yoga';
      case 'pilates': return 'Pilates';
      case 'functional': return 'Fonctionnel';
      default: return speciality;
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitCoach = (e) => {
    e.preventDefault();
    console.log('Nouveau coach:', formData);
    // Ici, on ajouterait le coach à la base de données
    setShowAddCoachModal(false);
    setFormData({
      firstName: '',
      lastName: '',
      speciality: 'musculation',
      experience: '',
      email: '',
      phone: '',
      status: 'active'
    });
  };

  const handleDeleteCoach = (coachId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce coach ?')) {
      console.log('Suppression du coach avec ID:', coachId);
      // Logique de suppression
    }
  };

  const handleAssignProgram = (e) => {
    e.preventDefault();
    console.log('Attribution de programme au coach:', selectedCoach.id);
    // Logique d'attribution
    setShowAssignProgramModal(false);
    setSelectedCoach(null);
  };

  const toggleCoachExpansion = (coachId) => {
    setExpandedCoach(expandedCoach === coachId ? null : coachId);
  };

  return (
    <div className="space-y-6">
      {/* En-tête et outils */}
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
        <div className="relative w-full max-w-md">
          <MagnifyingGlassIcon className="absolute left-3 top-3 h-5 w-5 text-white/50" />
          <input
            type="text"
            placeholder="Rechercher un coach..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        
        <button 
          onClick={() => setShowAddCoachModal(true)}
          className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl flex items-center gap-2 hover:from-purple-700 hover:to-indigo-700 transition-all shadow-lg"
        >
          <PlusIcon className="h-5 w-5" />
          <span>Ajouter un coach</span>
        </button>
      </div>
      
      {/* Statistiques des coachs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">Total coachs</p>
              <p className="text-2xl font-bold text-white">{mockCoaches.length}</p>
            </div>
            <AcademicCapIcon className="h-8 w-8 text-purple-400" />
          </div>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">Programmes assignés</p>
              <p className="text-2xl font-bold text-white">
                {mockCoaches.reduce((sum, coach) => sum + coach.programs.length, 0)}
              </p>
            </div>
            <UserGroupIcon className="h-8 w-8 text-blue-400" />
          </div>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">Membres supervisés</p>
              <p className="text-2xl font-bold text-white">
                {mockCoaches.reduce((sum, coach) => 
                  sum + coach.programs.reduce((pSum, program) => pSum + program.members, 0), 0
                )}
              </p>
            </div>
            <ArrowPathIcon className="h-8 w-8 text-green-400" />
          </div>
        </motion.div>
      </div>
      
      {/* Liste des coachs */}
      <div className="bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 overflow-hidden">
        <div className="p-6">
          <h2 className="text-xl font-semibold text-white mb-4">Coachs et leurs programmes</h2>
          
          <div className="space-y-4">
            {filteredCoaches.length > 0 ? (
              filteredCoaches.map((coach) => (
                <div 
                  key={coach.id} 
                  className="bg-white/5 rounded-xl border border-white/10 overflow-hidden"
                >
                  {/* Informations principales du coach */}
                  <div className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-full flex items-center justify-center">
                        <span className="text-white font-bold text-lg">
                          {coach.firstName.charAt(0)}{coach.lastName.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-white">{coach.firstName} {coach.lastName}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-white/60 text-sm">{getSpecialityName(coach.speciality)}</span>
                          <span className="text-white/30">•</span>
                          <span className="text-white/60 text-sm">{coach.experience}</span>
                          <span className="ml-2">{getStatusBadge(coach.status)}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          setSelectedCoach(coach);
                          setShowAssignProgramModal(true);
                        }}
                        className="p-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors"
                        title="Assigner un programme"
                      >
                        <PlusIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => {
                          setFormData({
                            firstName: coach.firstName,
                            lastName: coach.lastName,
                            speciality: coach.speciality,
                            experience: coach.experience,
                            email: coach.email,
                            phone: coach.phone,
                            status: coach.status
                          });
                          setShowAddCoachModal(true);
                        }}
                        className="p-2 bg-amber-500/20 text-amber-400 rounded-lg hover:bg-amber-500/30 transition-colors"
                        title="Modifier"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteCoach(coach.id)}
                        className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                        title="Supprimer"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => toggleCoachExpansion(coach.id)}
                        className="p-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
                        title={expandedCoach === coach.id ? "Masquer les programmes" : "Voir les programmes"}
                      >
                        {expandedCoach === coach.id ? (
                          <ChevronUpIcon className="h-4 w-4" />
                        ) : (
                          <ChevronDownIcon className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>
                  
                  {/* Programmes assignés (expandable) */}
                  {expandedCoach === coach.id && (
                    <div className="p-4 bg-white/5 border-t border-white/10">
                      <h4 className="text-sm font-medium text-white/80 mb-3">Programmes assignés ({coach.programs.length})</h4>
                      
                      {coach.programs.length > 0 ? (
                        <div className="space-y-2">
                          {coach.programs.map(program => (
                            <div key={program.id} className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
                              <div>
                                <span className="text-white font-medium">{program.name}</span>
                                <div className="flex items-center gap-1 mt-1">
                                  <UserGroupIcon className="h-3 w-3 text-blue-400" />
                                  <span className="text-white/60 text-xs">{program.members} membres</span>
                                </div>
                              </div>
                              <button className="p-1 bg-white/10 rounded-lg hover:bg-white/20 transition-colors">
                                <EyeIcon className="h-4 w-4 text-white/70" />
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-white/40 text-sm">Aucun programme assigné</p>
                      )}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="text-center py-12 bg-white/5 rounded-xl">
                <UserCircleIcon className="h-16 w-16 text-white/20 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white/60 mb-2">
                  Aucun coach trouvé
                </h3>
                <p className="text-white/40">
                  {searchTerm 
                    ? 'Essayez de modifier vos critères de recherche' 
                    : 'Ajoutez un coach pour commencer'
                  }
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Modal d'ajout/modification de coach */}
      {showAddCoachModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowAddCoachModal(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 max-w-xl w-full max-h-[80vh] overflow-y-auto"
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white">
                {formData.firstName ? 'Modifier le coach' : 'Ajouter un coach'}
              </h2>
              <button
                onClick={() => setShowAddCoachModal(false)}
                className="p-2 text-white/60 hover:text-white transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <form onSubmit={handleSubmitCoach} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-white/80 text-sm font-medium mb-2">
                    Prénom
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-white/80 text-sm font-medium mb-2">
                    Nom
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-white/80 text-sm font-medium mb-2">
                    Spécialité
                  </label>
                  <select
                    name="speciality"
                    value={formData.speciality}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="musculation" className="bg-gray-800">Musculation</option>
                    <option value="cardio" className="bg-gray-800">Cardio</option>
                    <option value="crossfit" className="bg-gray-800">CrossFit</option>
                    <option value="yoga" className="bg-gray-800">Yoga</option>
                    <option value="pilates" className="bg-gray-800">Pilates</option>
                    <option value="functional" className="bg-gray-800">Fonctionnel</option>
                  </select>
                </div>
                <div>
                  <label className="block text-white/80 text-sm font-medium mb-2">
                    Expérience
                  </label>
                  <input
                    type="text"
                    name="experience"
                    value={formData.experience}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ex: 5 ans"
                    required
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-white/80 text-sm font-medium mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-white/80 text-sm font-medium mb-2">
                    Téléphone
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-white/80 text-sm font-medium mb-2">
                  Statut
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="active" className="bg-gray-800">Actif</option>
                  <option value="vacation" className="bg-gray-800">En congé</option>
                  <option value="suspended" className="bg-gray-800">Suspendu</option>
                </select>
              </div>
              
              <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => setShowAddCoachModal(false)}
                  className="px-4 py-2 bg-white/10 text-white rounded-xl border border-white/20 hover:bg-white/20 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl hover:from-purple-700 hover:to-indigo-700 transition-all flex items-center gap-2"
                >
                  <CheckCircleIcon className="h-5 w-5" />
                  {formData.firstName ? 'Mettre à jour' : 'Ajouter le coach'}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
      
      {/* Modal d'assignation de programme */}
      {showAssignProgramModal && selectedCoach && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowAssignProgramModal(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 max-w-xl w-full max-h-[80vh] overflow-y-auto"
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white">
                Assigner un programme à {selectedCoach.firstName}
              </h2>
              <button
                onClick={() => setShowAssignProgramModal(false)}
                className="p-2 text-white/60 hover:text-white transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <form onSubmit={handleAssignProgram} className="space-y-4">
              <div className="mb-4">
                <label className="block text-white/80 text-sm font-medium mb-2">
                  Programme
                </label>
                
                <div className="space-y-2">
                  {mockPrograms.map(program => {
                    const isAssigned = selectedCoach.programs.some(p => p.id === program.id);
                    
                    return (
                      <label 
                        key={program.id}
                        className={`flex items-center justify-between p-3 rounded-xl cursor-pointer ${
                          isAssigned 
                            ? 'bg-blue-500/20 border-blue-500/50 border' 
                            : 'bg-white/10 hover:bg-white/15 border border-white/20'
                        }`}
                      >
                        <div className="flex items-center">
                          <input 
                            type="checkbox"
                            disabled={isAssigned}
                            className="w-5 h-5 rounded bg-white/20 border-white/40 text-blue-500 focus:ring-blue-500 focus:ring-offset-0 focus:ring-offset-transparent"
                          />
                          <div className="ml-3">
                            <span className="text-white font-medium">{program.name}</span>
                            <div className="flex items-center gap-1 mt-0.5">
                              <span className="text-white/60 text-xs">{program.category}</span>
                              <span className="text-white/30">•</span>
                              <span className={`text-xs ${
                                program.difficulty === 'beginner' ? 'text-green-400' :
                                program.difficulty === 'intermediate' ? 'text-yellow-400' : 'text-red-400'
                              }`}>
                                {program.difficulty === 'beginner' ? 'Débutant' :
                                 program.difficulty === 'intermediate' ? 'Intermédiaire' : 'Avancé'}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        {isAssigned && (
                          <span className="text-blue-400 text-xs">Déjà assigné</span>
                        )}
                      </label>
                    );
                  })}
                </div>
              </div>
              
              <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => setShowAssignProgramModal(false)}
                  className="px-4 py-2 bg-white/10 text-white rounded-xl border border-white/20 hover:bg-white/20 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all flex items-center gap-2"
                >
                  <CheckCircleIcon className="h-5 w-5" />
                  Assigner le programme
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default CoachesPrograms; 